<!-- fork overlay: fork-owned extension of OSS v4.15.1 (blob:35258af0b7cad27a) | Ported: 2026-07-02 -->
<!-- Gap-closure G1 (eva-captain-port D25): when the account runs native Eva (and not enterprise
Captain), the panel hosts Eva's CopilotPanel and relays its ActionCable rows from the bus. The
Captain branch below is upstream-untouched, so Captain accounts behave exactly as OSS. -->
<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import EvaCopilotPanel from 'dashboard/components-next/Eva/CopilotPanel.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('captainAssistants/getRecords');
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedCopilotThreadId = ref(null);
const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedAssistantId = ref(null);

// Captain keeps precedence: the Eva panel only takes the slot when the enterprise Captain copilot
// is not active, so a Captain account never sees a behavior change from this overlay.
const isCaptainCopilotEnabled = computed(
  () =>
    isEnterprise &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.CAPTAIN
    )
);
const isEvaCopilotEnabled = computed(
  () =>
    !isCaptainCopilotEnabled.value &&
    isFeatureEnabledonAccount.value(currentAccountId.value, 'eva')
);

// The Dashboard mount passes no conversationInboxType prop, so derive it from the selected chat
// (meta.channel = the inbox channel type, same source ContactPanel uses) — it decides whether a
// suggested reply inserts through the rich (Web/Email) or plain editor path.
const evaConversationInboxType = computed(
  () => props.conversationInboxType || currentChat.value?.meta?.channel || ''
);

// Streamed Eva copilot rows arrive on the bus (fork actionCable.js overlay) and are forwarded to
// the mounted panel, which filters by its own thread id.
const evaPanel = ref(null);
const onEvaCopilotMessage = data => evaPanel.value?.appendStreamedMessage(data);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_captain_assistant_id;

  // If the user has selected a specific assistant, it takes first preference for Copilot.
  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    // Return the preferred assistant if found, otherwise continue to next cases
    if (preferredAssistant) return preferredAssistant;
  }

  // If the above is not available, the assistant connected to the inbox takes preference.
  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  // If neither of the above is available, the first assistant in the account takes preference.
  return assistants.value[0];
});

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_captain_assistant_id: assistant.id,
  });
};

const shouldShowCopilotPanel = computed(() => {
  const { is_copilot_panel_open: isCopilotPanelOpen } = uiSettings.value;
  if (!isCopilotPanelOpen) return false;
  if (isEvaCopilotEnabled.value) return true;
  if (!isEnterprise) {
    return false;
  }
  return isCaptainCopilotEnabled.value && !uiFlags.value.fetchingList;
});

// Eva is conversation-grounded: with no conversation selected (list views, contacts, reports) an
// open panel is a leftover from the last conversation and just confuses — hide it (v-show, so the
// per-conversation exchanges survive) until a conversation is selected again. Captain keeps
// upstream behavior (its copilot also serves dashboard-level prompts).
const showEvaPanel = computed(
  () => shouldShowCopilotPanel.value && Boolean(currentChat.value?.id)
);

// The Eva branch stays mounted while hidden (v-show), so its click-outside directive stays armed —
// and the directive fires on `click`, AFTER the toggle's own handler has synchronously opened the
// panel, so the very click that opens it would close it again on small screens. Latch visibility at
// pointerdown (which fires before any click handler): only a click that STARTED while the panel was
// visible may close it. The Captain branch keeps upstream v-if semantics and needs none of this.
const evaPanelVisibleAtPointerdown = ref(false);
const rememberEvaPanelVisibility = () => {
  evaPanelVisibleAtPointerdown.value = showEvaPanel.value;
};
const onClickOutsideEvaPanel = () => {
  if (!evaPanelVisibleAtPointerdown.value) return;
  closeCopilotPanel();
};

const handleReset = () => {
  selectedCopilotThreadId.value = null;
};

const sendMessage = async message => {
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      selectedCopilotThreadId.value = response.id;
    }
  } catch (error) {
    useAlert(error.message);
  }
};

onMounted(() => {
  if (isEnterprise) {
    store.dispatch('captainAssistants/get');
  }
  emitter.on('eva.copilot.message.created', onEvaCopilotMessage);
  window.addEventListener('pointerdown', rememberEvaPanelVisibility, true);
});

onBeforeUnmount(() => {
  emitter.off('eva.copilot.message.created', onEvaCopilotMessage);
  window.removeEventListener('pointerdown', rememberEvaPanelVisibility, true);
});
</script>

<template>
  <!-- Eva branch uses v-show (not v-if): the panel holds its per-conversation exchanges locally,
  so it must stay MOUNTED while the agent toggles the side panel closed or navigates around — the
  active exchanges and replies streamed while hidden survive. Conversation switches are handled
  inside the panel (session cache keyed by conversation id), so no :key remount here. -->
  <div
    v-if="isEvaCopilotEnabled"
    v-show="showEvaPanel"
    v-on-click-outside="onClickOutsideEvaPanel"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': showEvaPanel,
        'md:hidden': !showEvaPanel,
      },
    ]"
  >
    <EvaCopilotPanel
      ref="evaPanel"
      :account-id="currentAccountId"
      :conversation-id="currentChat?.id"
      :conversation-inbox-type="evaConversationInboxType"
      class="h-full"
    />
  </div>
  <!-- Captain branch: upstream semantics untouched. -->
  <div
    v-else-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <Copilot
      :messages="messages"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      @set-assistant="setAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
    />
  </div>
  <template v-else />
</template>
